/**
 * Client-side filtering and sorting utilities for SpmTableAPI
 * Used when mode='client' to process data locally instead of via API
 */

import { SpmFilterOperator } from './filters/operators';
import type { SpmActiveFilter, SpmActiveSort } from './filters/types';

/**
 * Apply a single filter to a value
 */
const applyFilterOperator = (
  value: unknown,
  operator: SpmFilterOperator | string,
  filterValues: unknown,
): boolean => {
  // Handle null/undefined values
  if (value === null || value === undefined) {
    return operator === SpmFilterOperator.NOT_EQUALS
      || operator === SpmFilterOperator.NOT_CONTAINS
      || operator === SpmFilterOperator.NOT_IN;
  }

  const stringValue = String(value).toLowerCase();
  const filterValuesArray = Array.isArray(filterValues) ? filterValues : [filterValues];

  switch (operator) {
    case SpmFilterOperator.EQUALS:
      return filterValuesArray.some((fv) => String(fv).toLowerCase() === stringValue);

    case SpmFilterOperator.NOT_EQUALS:
      return !filterValuesArray.some((fv) => String(fv).toLowerCase() === stringValue);

    case SpmFilterOperator.CONTAINS:
      return filterValuesArray.some((fv) => stringValue.includes(String(fv).toLowerCase()));

    case SpmFilterOperator.NOT_CONTAINS:
      return !filterValuesArray.some((fv) => stringValue.includes(String(fv).toLowerCase()));

    case SpmFilterOperator.STARTS_WITH:
      return filterValuesArray.some((fv) => stringValue.startsWith(String(fv).toLowerCase()));

    case SpmFilterOperator.ENDS_WITH:
      return filterValuesArray.some((fv) => stringValue.endsWith(String(fv).toLowerCase()));

    case SpmFilterOperator.LIKE:
      // Simple LIKE implementation: treat % as wildcard
      return filterValuesArray.some((fv) => {
        const pattern = String(fv).toLowerCase().replace(/%/g, '.*');
        const regex = new RegExp(`^${pattern}$`, 'i');
        return regex.test(stringValue);
      });

    case SpmFilterOperator.IN:
      return filterValuesArray.some((fv) => String(fv).toLowerCase() === stringValue);

    case SpmFilterOperator.NOT_IN:
      return !filterValuesArray.some((fv) => String(fv).toLowerCase() === stringValue);

    case SpmFilterOperator.LESS_THAN: {
      const numValue = Number(value);
      const numFilter = Number(filterValuesArray[0]);
      return !Number.isNaN(numValue) && !Number.isNaN(numFilter) && numValue < numFilter;
    }

    case SpmFilterOperator.LESS_THAN_OR_EQUAL_TO: {
      const numValue = Number(value);
      const numFilter = Number(filterValuesArray[0]);
      return !Number.isNaN(numValue) && !Number.isNaN(numFilter) && numValue <= numFilter;
    }

    case SpmFilterOperator.GREATER_THAN: {
      const numValue = Number(value);
      const numFilter = Number(filterValuesArray[0]);
      return !Number.isNaN(numValue) && !Number.isNaN(numFilter) && numValue > numFilter;
    }

    case SpmFilterOperator.GREATER_THAN_OR_EQUAL_TO: {
      const numValue = Number(value);
      const numFilter = Number(filterValuesArray[0]);
      return !Number.isNaN(numValue) && !Number.isNaN(numFilter) && numValue >= numFilter;
    }

    case SpmFilterOperator.BETWEEN: {
      if (filterValuesArray.length < 2) return true;
      const numValue = Number(value);
      const min = Number(filterValuesArray[0]);
      const max = Number(filterValuesArray[1]);
      if (Number.isNaN(numValue)) {
        // Try date comparison
        const dateValue = new Date(value as string).getTime();
        const dateMin = new Date(filterValuesArray[0] as string).getTime();
        const dateMax = new Date(filterValuesArray[1] as string).getTime();
        if (!Number.isNaN(dateValue) && !Number.isNaN(dateMin) && !Number.isNaN(dateMax)) {
          return dateValue >= dateMin && dateValue <= dateMax;
        }
        return true;
      }
      return numValue >= min && numValue <= max;
    }

    case SpmFilterOperator.NOT_BETWEEN: {
      if (filterValuesArray.length < 2) return true;
      const numValue = Number(value);
      const min = Number(filterValuesArray[0]);
      const max = Number(filterValuesArray[1]);
      if (Number.isNaN(numValue)) {
        // Try date comparison
        const dateValue = new Date(value as string).getTime();
        const dateMin = new Date(filterValuesArray[0] as string).getTime();
        const dateMax = new Date(filterValuesArray[1] as string).getTime();
        if (!Number.isNaN(dateValue) && !Number.isNaN(dateMin) && !Number.isNaN(dateMax)) {
          return dateValue < dateMin || dateValue > dateMax;
        }
        return true;
      }
      return numValue < min || numValue > max;
    }

    default:
      return true;
  }
};

/**
 * Apply all active filters to a dataset
 * @param data - Array of data objects
 * @param filters - Array of active filters
 * @returns Filtered data array
 */
export const applyClientFilters = <T extends Record<string, unknown>>(
  data: T[],
  filters: SpmActiveFilter[],
): T[] => {
  if (!filters || filters.length === 0) {
    return data;
  }

  return data.filter((item) => filters.every((filter) => {
    const value = item[filter.field];
    return applyFilterOperator(value, filter.operator, filter.values);
  }));
};

/**
 * Apply sorting to a dataset
 * @param data - Array of data objects
 * @param sort - Active sort configuration
 * @returns Sorted data array
 */
export const applyClientSort = <T extends Record<string, unknown>>(
  data: T[],
  sort: SpmActiveSort | null,
): T[] => {
  if (!sort) {
    return data;
  }

  const sortedData = [...data];
  const { field, order } = sort;
  const multiplier = order === 'asc' ? 1 : -1;

  sortedData.sort((a, b) => {
    const valueA = a[field];
    const valueB = b[field];

    // Handle null/undefined
    if (valueA === null || valueA === undefined) return 1 * multiplier;
    if (valueB === null || valueB === undefined) return -1 * multiplier;

    // Try numeric comparison
    const numA = Number(valueA);
    const numB = Number(valueB);
    if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
      return (numA - numB) * multiplier;
    }

    // Try date comparison
    const dateA = new Date(valueA as string).getTime();
    const dateB = new Date(valueB as string).getTime();
    if (!Number.isNaN(dateA) && !Number.isNaN(dateB)) {
      return (dateA - dateB) * multiplier;
    }

    // String comparison
    const strA = String(valueA).toLowerCase();
    const strB = String(valueB).toLowerCase();
    return strA.localeCompare(strB) * multiplier;
  });

  return sortedData;
};

/**
 * Apply pagination to a dataset
 * @param data - Array of data objects
 * @param offset - Starting index
 * @param limit - Number of items per page
 * @returns Paginated data array
 */
export const applyClientPagination = <T>(
  data: T[],
  offset: number,
  limit: number,
): T[] => data.slice(offset, offset + limit);
